/**
 * Utilitário centralizado para preparar credenciais SMB para envio ao CorelDRAW
 *
 * Garante formato consistente e validação em todos os fluxos:
 * - Sempre usa "password" (nunca "password_enc") no payload
 * - Valida username e password obrigatórios
 * - Domain apenas se não-null/não-vazio
 * - Detecta automaticamente se caminho é UNC e precisa SMB
 */

import {
	smbCredentialsService,
	type SmbCredential,
} from "../services/smbCredentialsService";
import { sendToTerminal } from "../lib/terminalLogger";

/**
 * Interface para credenciais SMB formatadas para payload
 */
export interface SmbCredentialsPayload {
	username: string;
	password: string; // ✅ SEMPRE "password" (nunca "password_enc")
	domain?: string; // Opcional, apenas se não-null/não-vazio
}

/**
 * Resultado da preparação de credenciais SMB (diferencia erro de ausência)
 */
export interface SmbCredentialsResult {
	/** Se a operação foi bem-sucedida (GET retornou 200) */
	success: boolean;
	/** Se houve credenciais retornadas (success=true + hasCredentials=true = obrigatório incluir) */
	hasCredentials: boolean;
	/** Credenciais formatadas (apenas se hasCredentials=true) */
	credentials?: SmbCredentialsPayload;
	/** Mensagem de erro (apenas se success=false) */
	error?: string;
	/** Tipo de erro para tratamento específico */
	errorType?: "network" | "server" | "validation" | "not_found";
}

/**
 * Opções para preparar credenciais SMB
 */
export interface PrepareSmbOptions {
	/** Fonte das credenciais: 'rules' (regras de produto/pedido) ou 'credential' (API direta) */
	source: "rules" | "credential";
	/** Regras do produto/pedido (para source: 'rules') */
	rules?: any;
	/** ID da credencial (para source: 'credential') */
	credentialId?: number;
	/** Caminho do arquivo/diretório (para detectar se é UNC) */
	path?: string;
	/** Contexto para logs (ex: 'ABRIR-ARQUIVO', 'EXPORT-PNG') */
	context?: string;
	/** Se deve buscar credenciais mesmo se caminho não for UNC (default: false) */
	forceSmb?: boolean;
	/** Número de tentativas de retry em caso de erro (default: 3) */
	maxRetries?: number;
	/** Delay inicial entre retries em ms (default: 500ms, com backoff exponencial) */
	retryDelay?: number;
}

/**
 * Sleep helper para retry
 */
function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Verifica se um caminho é UNC (rede)
 */
function isUncPath(path: string | undefined | null): boolean {
	if (!path) return false;
	const normalized = String(path).trim();
	return normalized.startsWith("\\\\") || normalized.startsWith("//");
}

/**
 * Extrai credenciais SMB brutas das regras
 */
function extractSmbFromRules(rules: any): any {
	if (!rules) return null;

	// ✅ Ordem de prioridade CORRIGIDA (seguranca.smb PRIMEIRO)
	// SalesOrdersPage usa: rules.seguranca.smb (retornado por /cdr/rules/:pro_id)
	return (
		rules?.seguranca?.smb || // ← PRIORIDADE 1: Padrão atual do SalesOrdersPage
		rules?.security?.smb ||
		rules?.security?.interno?.smb ||
		rules?.security?.exportprod?.smb ||
		rules?.security?.exportpng?.smb ||
		rules?.security?.diretorio_base_interno?.smb || // ← Agora é o ÚLTIMO (credenciais antigas)
		rules?.security?.diretorio_base_exportpng?.smb ||
		rules?.security?.externo?.smb
	);
}

/**
 * Valida e formata credenciais SMB para payload
 *
 * @param smbRaw Credenciais brutas (pode ter password ou password_enc)
 * @param context Contexto para logs
 * @returns Credenciais formatadas ou null se inválidas
 */
function formatSmbCredentials(
	smbRaw: any,
	context?: string,
): SmbCredentialsPayload | null {
	if (!smbRaw) return null;

	// Validar username
	const username = smbRaw?.username ? String(smbRaw.username).trim() : null;
	if (!username || username.length === 0) {
		if (context) {
			try {
				sendToTerminal(`⚠️ [${context}:SMB] Username ausente ou vazio`, {
					hasSmb: !!smbRaw,
				});
			} catch {}
		}
		return null;
	}

	// Validar password (prioriza password, depois password_enc)
	const passwordValue = smbRaw?.password || smbRaw?.password_enc;
	const password = passwordValue ? String(passwordValue).trim() : null;
	if (!password || password.length === 0) {
		if (context) {
			try {
				sendToTerminal(`⚠️ [${context}:SMB] Password ausente ou vazio`, {
					hasUsername: !!username,
				});
			} catch {}
		}
		return null;
	}

	// Formatar payload
	const smbPayload: SmbCredentialsPayload = {
		username: username,
		password: password, // ✅ SEMPRE usar "password" (não password_enc)
	};

	// ✅ Domain: apenas se não-null E não-vazio (conforme requisito do usuário)
	const domainValue = smbRaw?.domain;
	if (
		domainValue !== null &&
		domainValue !== undefined &&
		typeof domainValue === "string"
	) {
		const domainTrimmed = domainValue.trim();
		if (domainTrimmed.length > 0) {
			smbPayload.domain = domainTrimmed;
		}
		// ⚠️ Se domain for null, undefined ou string vazia, NÃO inclui no payload
	}

	// Log removido - validação de credenciais é operação rotineira

	return smbPayload;
}

/**
 * Prepara credenciais SMB para envio ao CorelDRAW (Nova Lógica v2.0)
 *
 * **Regras de Negócio:**
 * 1. GET com sucesso + credenciais retornadas → SMB é OBRIGATÓRIO no payload
 * 2. GET com erro → Retry automático → Se falhar, BLOQUEIA salvamento
 * 3. GET com sucesso mas SEM credenciais → Continua SEM SMB (caminho não requer auth)
 *
 * Esta função centraliza a lógica de:
 * 1. Detectar se caminho é UNC (precisa SMB)
 * 2. Buscar credenciais da fonte correta (regras ou API)
 * 3. Retry automático em caso de erro (com backoff exponencial)
 * 4. Validar username e password
 * 5. Formatar para payload (sempre usar "password", não "password_enc")
 * 6. Incluir domain apenas se não-null/não-vazio
 *
 * @param options Opções para preparar credenciais
 * @returns Resultado estruturado com sucesso/erro/credenciais
 */
export async function prepareSmbCredentials(
	options: PrepareSmbOptions,
): Promise<SmbCredentialsResult> {
	const {
		source,
		rules,
		credentialId,
		path,
		context,
		forceSmb = false,
		maxRetries = 3,
		retryDelay = 500,
	} = options;

	// 1. Detectar se precisa SMB (se path fornecido, verificar se é UNC)
	if (path && !forceSmb && !isUncPath(path)) {
		if (context) {
			try {
				sendToTerminal(`ℹ️ [${context}:SMB] Caminho local - não requer SMB`, {
					path,
				});
			} catch {}
		}
		return {
			success: true,
			hasCredentials: false,
		};
	}

	let smbRaw: any = null;
	let lastError: any = null;

	// 2. Buscar credenciais da fonte correta
	if (source === "credential" && credentialId) {
		// 🔁 Buscar da API com retry
		for (let attempt = 1; attempt <= maxRetries; attempt++) {
			try {
				// Log removido - busca de credenciais é operação rotineira

				const credential: SmbCredential =
					await smbCredentialsService.getCredential(credentialId);

				// ✅ Sucesso: formatar para estrutura compatível
				smbRaw = {
					username: credential.username,
					password: credential.password_enc, // API retorna password_enc
					password_enc: credential.password_enc,
					domain: credential.domain,
				};

				// Log removido - sucesso na obtenção de credenciais é rotineiro

				// Sucesso - sai do loop de retry
				break;
			} catch (error) {
				lastError = error;
				const errorMsg =
					error instanceof Error ? error.message : "Erro desconhecido";

				if (context) {
					try {
						sendToTerminal(
							`⚠️ [${context}:SMB] Tentativa ${attempt}/${maxRetries} falhou`,
							{
								error: errorMsg,
								credentialId,
								willRetry: attempt < maxRetries,
							},
						);
					} catch {}
				}

				// Se não é a última tentativa, aguarda antes de retry (backoff exponencial)
				if (attempt < maxRetries) {
					const delay = retryDelay * Math.pow(2, attempt - 1); // 500ms, 1s, 2s...
					if (context) {
						try {
							sendToTerminal(
								`⏳ [${context}:SMB] Aguardando ${delay}ms antes de retry...`,
							);
						} catch {}
					}
					await sleep(delay);
				}
			}
		}

		// ❌ Se chegou aqui sem smbRaw, todas as tentativas falharam
		if (!smbRaw) {
			const finalError =
				lastError instanceof Error
					? lastError.message
					: "Erro desconhecido ao buscar credenciais SMB";

			if (context) {
				try {
					sendToTerminal(
						`❌ [${context}:SMB] Falha após ${maxRetries} tentativas - BLOQUEANDO operação`,
						{
							error: finalError,
							credentialId,
							maxRetries,
						},
					);
				} catch {}
			}

			return {
				success: false,
				hasCredentials: false,
				error: finalError,
				errorType: "server",
			};
		}
	} else if (source === "rules" && rules) {
		// Buscar das regras (sem retry pois é local)
		smbRaw = extractSmbFromRules(rules);

		// ℹ️ Para source='rules': ausência ou erro = continuar sem SMB (não bloquear)
		if (!smbRaw) {
			if (context) {
				try {
					sendToTerminal(
						`ℹ️ [${context}:SMB] Sem credenciais SMB nas regras - continuando`,
						{},
					);
				} catch {}
			}
			return {
				success: true,
				hasCredentials: false,
			};
		}

		// Log removido - credenciais encontradas nas regras é operação rotineira
	} else {
		if (context) {
			try {
				sendToTerminal(
					`ℹ️ [${context}:SMB] Fonte de credenciais não especificada`,
					{ source },
				);
			} catch {}
		}
		return {
			success: true,
			hasCredentials: false,
		};
	}

	// 3. Validar e formatar (se encontrou credenciais)
	if (!smbRaw) {
		// ✅ Sem credenciais = não precisa SMB
		if (context) {
			try {
				sendToTerminal(`✅ [${context}:SMB] Operação OK - sem credenciais`, {});
			} catch {}
		}
		return {
			success: true,
			hasCredentials: false,
		};
	}

	const formatted = formatSmbCredentials(smbRaw, context);

	if (!formatted) {
		// ⚠️ Credenciais encontradas mas inválidas
		// Para source='rules': WARNING mas continua
		// Para source='credential': deveria ter bloqueado antes (no retry)
		if (context) {
			if (source === "rules") {
				try {
					sendToTerminal(
						`⚠️ [${context}:SMB] Credenciais nas regras inválidas - continuando sem SMB`,
						{
							hasUsername: !!smbRaw?.username,
							hasPassword: !!(smbRaw?.password || smbRaw?.password_enc),
						},
					);
				} catch {}
			} else {
				try {
					sendToTerminal(
						`❌ [${context}:SMB] Credenciais inválidas - BLOQUEANDO`,
						{
							hasUsername: !!smbRaw?.username,
							hasPassword: !!(smbRaw?.password || smbRaw?.password_enc),
						},
					);
				} catch {}
			}
		}

		// ✅ Para source='rules': continuar sem SMB mesmo se inválidas
		if (source === "rules") {
			return {
				success: true,
				hasCredentials: false,
			};
		}

		// ❌ Para source='credential': bloquear se inválidas
		return {
			success: false,
			hasCredentials: false,
			error: "Credenciais SMB inválidas (username ou password ausente/vazio)",
			errorType: "validation",
		};
	}

	// ✅ Sucesso com credenciais válidas
	// Log removido - validação bem-sucedida é operação rotineira

	return {
		success: true,
		hasCredentials: true,
		credentials: formatted,
	};
}

/**
 * Função auxiliar para verificar se caminho é UNC
 */
export function isUncPathHelper(path: string | undefined | null): boolean {
	return isUncPath(path);
}
